import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';

import { LayoutWrapper } from '../../../worker-app/layouts';
import { useCvParsingJobStatus, useFetchWorkerDataQuery } from '../../hooks';
import { Header } from './header';
import { setWorkerFieldOptionsAtom } from '../global-state.worker-data';
import ToastMessage from '../toast-message';
import { useWorkerFieldOptions } from '../worker-application-wizard';
import WorkerManagedUsingKlaaryo, {
  workerManagedFooterMapKeyAtom,
  workerManagedUsingKlaaryoModalFlagAtom,
} from '../worker-managed-using-klaaryo';
import { ProfileDetailsSection } from '../worker-references';
import { UserProfileDetails } from './user-profile-details';
import { cn } from '@restworld/utils-common';

export function ProfileComponent() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const setWorkerManagedUsingKlaaryoModalFlagAtom = useSetAtom(workerManagedUsingKlaaryoModalFlagAtom);
  const setWorkerManagedFooterMapKeyAtom = useSetAtom(workerManagedFooterMapKeyAtom);

  const router = useRouter();

  useFetchWorkerDataQuery();
  useCvParsingJobStatus();

  const options = useWorkerFieldOptions({ isFetchStatusOptions: false });
  const onSetWorkerFieldOptionsData = useSetAtom(setWorkerFieldOptionsAtom);

  useEffect(() => {
    if (options) onSetWorkerFieldOptionsData(options);
  }, [options, onSetWorkerFieldOptionsData]);

  useEffect(() => {
    if (router.query?.manage_using_klaaryo === 'true') {
      setWorkerManagedUsingKlaaryoModalFlagAtom(true);
      setWorkerManagedFooterMapKeyAtom('profile');
    }
  }, [router.query?.manage_using_klaaryo, setWorkerManagedFooterMapKeyAtom, setWorkerManagedUsingKlaaryoModalFlagAtom]);

  const showReferenceFeature = process.env.NEXT_PUBLIC_SHOW_REFERENCES_FEATURE === 'true';

  return (
    <>
      <LayoutWrapper
        title={t('onboarding-page:profile')}
        hasFooter
        hasNavbar
        showSearchBarInNavbar={user?.type !== 'employer'}
        testId="navbar-for-profile-page"
        className="bg-neutral-10"
      >
        <ProfileWrapper>
          <ToastMessage />
          <div className="box-border flex lg:flex-row flex-col-reverse  lg:justify-center justify-start  lg:gap-10 gap-8 w-full max-w-full">
            <div className="flex flex-col items-start flex-1">
              <Header />
            </div>

            <div className="flex flex-row lg:justify-start md:justify-center justify-start">
              {showReferenceFeature ? <ProfileDetailsSection /> : <UserProfileDetails />}
            </div>
          </div>
        </ProfileWrapper>
      </LayoutWrapper>
      <WorkerManagedUsingKlaaryo />
    </>
  );
}

const ProfileWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative flex box-border flex-col items-center max-w-full', className)} {...props} />
);
